import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import useMyArray from "../../../hooks/useMyArray";
import Select from "../Select";
import ServerAutoSuggest from "../ServerAutoSuggest";

const getMandatoryEnv = require("../../../utils/getMandatoryEnv");

const { mySrcmConfig } = getMandatoryEnv(["mySrcmConfig"]);
const dflt = [];
const getPostalCode = async (input) => {
  if (typeof input === "string" && input.endsWith("^")) {
    const s = input.split("^");
    const o = { id: s[0], pincode: s[0], state: "", country: "" };
    return Promise.resolve(o);
  }
  const o = { id: input || "", pincode: input || "", state: "", country: "" };
  return Promise.resolve(o);
};

function transformCompleteName(completeName) {
  const parts = [
    ...new Set(completeName?.split("/").map((part) => part?.trim())),
  ];
  return parts.reverse().join(", ");
}

const GroupChildrenSelection = ({
  name,
  city,
  includeOnly,
  options,
  value,
  valueDisplay,
  siblingsOf,
  siblingsType,
  ...restprops
}) => {
  const [myOptions, { set }] = useMyArray(options);
  useEffect(() => {
    if (siblingsOf && siblingsType && (value || restprops.edit)) {
      const fetchSiblings = async () => {
        const response = await fetch(
          siblingsType === "city"
            ? `${mySrcmConfig?.profileServer}/api/v2/pincodes/?city_id=${siblingsOf}`
            : `${mySrcmConfig?.profileServer}/api/v2/cities/?city_type=place&pincode_id=${siblingsOf}&page_size=200`
        );
        const data = await response.json();
        let formattedOptions = (data?.results || []).map((item) => ({
          name: item?.pincode ? item?.pincode : item?.id,
          label: transformCompleteName(item?.complete_name) || item?.pincode,
        }));
        if (formattedOptions.length === 0) {
          formattedOptions = [
            { name: "no-data", label: "No data found", disabled: true },
          ];
        }
        set(formattedOptions, (r) => r?.name);
      };

      fetchSiblings();
    }
  }, [siblingsOf, siblingsType, set, value, restprops.edit]);

  return (
    <Select name={name} {...restprops} value={value} options={myOptions} />
  );
};

GroupChildrenSelection.propTypes = {
  name: PropTypes.string.isRequired,
  options: PropTypes.shape([]),
  value: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  valueDisplay: PropTypes.string,
  siblingsOf: PropTypes.number,
  siblingsType: PropTypes.string,
  city: PropTypes.objectOf(PropTypes.any),
  includeOnly: PropTypes.func,
};

GroupChildrenSelection.defaultProps = {
  options: [],
  value: undefined,
  valueDisplay: undefined,
  siblingsOf: null,
  siblingsType: null,
  city: { id: "", name: "" },
  includeOnly: null,
};

const CityZip = ({
  name,
  city,
  siblingsOf,
  includeOnly,
  siblingisIndia,
  siblingsType,
  ...restprops
}) => {
  const [error, setError] = useState("");

  const validatePostalCode = (value) => {
    const postalCodeRegex = /^[A-Za-z0-9-]{3,10}$/;
    if (!postalCodeRegex.test(value)) {
      return "Invalid postal code. Only letters, numbers, and '-' are allowed.";
    }
    return "";
  };
  const getConfigsObj = useMemo(() => {
    const config = {
      dflt: includeOnly ? dflt.filter(includeOnly) : dflt,
      fetch: getPostalCode,
      getSuggestionValue: (suggestion) => {
        const selectedPincode =
          suggestion?.pincode ||
          (suggestion?.results?.length > 0 && suggestion.results[0]?.pincode) ||
          "";
        return `${typeof selectedPincode === "string" ? selectedPincode : ""}`;
      },
      renderSuggestion: (suggestion) => suggestion?.pincode,
      api: (itrimValue) => {
        const errorMessage = validatePostalCode(itrimValue);
        if (errorMessage) {
          setError(errorMessage);
          return "";
        }
        setError("");
        return `${mySrcmConfig?.profileServer}/api/v2/pincodes/${
          city ? `?city_id=${city?.id}&` : `?`
        }pincode__startswith=${itrimValue}`;
      },

      data2resultsAsync: async (data, itrimValue) => {
        if (!!itrimValue && data?.results?.length === 0) {
          return [
            {
              id: `${itrimValue}`,
              pincode: itrimValue,
            },
          ];
        }
        return [...data?.results];
      },
    };

    return config;
  }, [city, includeOnly]);
  const updateCityZipLabel = (fieldType, isIndia) => {
    if (typeof document !== "undefined") {
      const labelElement = document.getElementById("city_zip_label");
      if (labelElement) {
        if (fieldType === "zip") {
          labelElement.innerHTML = "Select City/Locality *";
        } else if (isIndia) {
          labelElement.innerHTML = "Select Postal Code *";
        } else {
          labelElement.innerHTML = "Enter Postal Code";
        }
      }
    }
  };

  useEffect(() => {
    if (siblingsType) {
      updateCityZipLabel(siblingsType, siblingisIndia);
    }
  }, [siblingsType, siblingisIndia]);
  return (
    <div>
      {!(city?.isIndia ?? true) || !siblingisIndia ? (
        <>
          <ServerAutoSuggest {...restprops} config={getConfigsObj} />
          {error && (
            <span style={{ color: "red", fontSize: "12px" }}>{error}</span>
          )}
        </>
      ) : (
        <GroupChildrenSelection
          name={name}
          city={city}
          includeOnly={includeOnly}
          siblingsOf={siblingsOf}
          siblingsType={siblingsType}
          {...restprops}
        />
      )}
    </div>
  );
};

CityZip.propTypes = {
  country: PropTypes.string,
  edit: PropTypes.bool,
  name: PropTypes.string.isRequired,
  siblingsOf: PropTypes.number,
  siblingsType: PropTypes.string,
  siblingisIndia: PropTypes.bool,
  city: PropTypes.objectOf(PropTypes.any),
  includeOnly: PropTypes.func,
};

CityZip.defaultProps = {
  country: "",
  edit: false,
  siblingsOf: null,
  siblingsType: null,
  siblingisIndia: true,
  city: { id: "", name: "" },
  includeOnly: null,
};

export default CityZip;
